import { useEffect, useMemo, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { youtubeId } from "@/utils/youtube";
import { MeasurementSubmitForm } from "@/components/guides/MeasurementSubmitForm";

const BACKEND_URL =
  window.APP_CONFIG?.BACKEND_URL ||
  process.env.REACT_APP_BACKEND_URL ||
  window.location.origin;
const API = `${BACKEND_URL}/api`;

/**
 * Public advertorial — /learn/:slug.
 *
 * No auth, no sticky bottom nav. Educational editorial layout with a
 * sticky mobile CTA bar that routes back into the Instant Estimate
 * funnel with full UTM attribution so leads / estimates / invoices /
 * revenue roll up to the article in the dashboard.
 *
 * The whole doc is a tree of typed blocks (heading / paragraph /
 * image / comparison_table / faq / cta_button / testimonial_placeholder
 * / related_articles) — the admin edits that JSON, we render each
 * block with a small switch.
 */
export default function AdvertorialArticle() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [article, setArticle] = useState(null);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);
  const viewLogged = useRef(false);

  // Preserve any UTM params the visitor arrived with. Everything we
  // forward to the Instant Estimate funnel gets them + the article's
  // own slug / id, so attribution survives both inbound (ad → article)
  // and outbound (article → quote) hops.
  const incomingUtm = useMemo(() => {
    const sp = new URLSearchParams(window.location.search);
    return {
      utm_source: sp.get("utm_source") || "advertorial",
      utm_medium: sp.get("utm_medium") || "article",
      utm_campaign: sp.get("utm_campaign") || slug,
      utm_content: sp.get("utm_content"),
      utm_term: sp.get("utm_term"),
    };
  }, [slug]);

  useEffect(() => {
    axios
      .get(`${API}/public/advertorials/${slug}`)
      .then((res) => {
        setArticle(res.data);
        return axios.get(`${API}/public/advertorials?kind=${res.data.kind === "guide" ? "guide" : "advertorial"}`);
      })
      .then((res) => setRelated(res?.data || []))
      .catch(() => setArticle((a) => a || null))
      .finally(() => setLoading(false));
  }, [slug]);
  const isGuide = article?.kind === "guide";

  // Fire one "view" event per mount, with the UTM payload the visitor
  // arrived with. Guarded by a ref so React.StrictMode double-mounts
  // don't double-count.
  useEffect(() => {
    if (!article || viewLogged.current) return;
    viewLogged.current = true;
    const payload = {
      event: "view",
      utm_source: incomingUtm.utm_source,
      utm_medium: incomingUtm.utm_medium,
      utm_campaign: incomingUtm.utm_campaign,
      utm_content: incomingUtm.utm_content || article.article_id,
      utm_term: incomingUtm.utm_term,
    };
    axios
      .post(`${API}/public/advertorials/${article.article_id}/events`, payload)
      .catch(() => {});
  }, [article, incomingUtm]);

  // On the client we also set <title> / meta description so humans
  // sharing *while* on the page get the right preview from browser
  // extensions. (Social crawlers get the server-rendered head via the
  // backend middleware.)
  useEffect(() => {
    if (!article) return;
    document.title = article.seo_title || article.title || "ScreenzZ";
    const desc = article.seo_description || article.description || "";
    let m = document.querySelector('meta[name="description"]');
    if (!m) {
      m = document.createElement("meta");
      m.name = "description";
      document.head.appendChild(m);
    }
    m.setAttribute("content", desc);
    return () => {
      document.title = "ScreenzZ";
    };
  }, [article]);

  const quoteHref = (preferredProductOverride) => {
    const pref =
      preferredProductOverride ||
      article?.preferred_product ||
      null;
    const sp = new URLSearchParams();
    sp.set("utm_source", incomingUtm.utm_source || "advertorial");
    sp.set("utm_medium", incomingUtm.utm_medium || "article");
    sp.set("utm_campaign", slug || "");
    sp.set("utm_content", article?.article_id || "");
    if (incomingUtm.utm_term) sp.set("utm_term", incomingUtm.utm_term);
    if (pref) sp.set("pref", pref);
    return `/instant-estimate?${sp.toString()}`;
  };

  const trackCta = () => {
    if (!article) return;
    axios
      .post(`${API}/public/advertorials/${article.article_id}/events`, {
        event: "cta_click",
        utm_source: incomingUtm.utm_source,
        utm_medium: incomingUtm.utm_medium,
        utm_campaign: slug,
        utm_content: article.article_id,
      })
      .catch(() => {});
  };

  const goToQuote = (pref) => {
    trackCta();
    navigate(quoteHref(pref));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-50 flex items-center justify-center text-zinc-500">
        Loading…
      </div>
    );
  }
  if (!article) {
    return (
      <div className="min-h-screen bg-zinc-50 flex flex-col items-center justify-center p-6 text-center">
        <h1 className="text-2xl font-bold text-zinc-900 mb-2">Article not found</h1>
        <p className="text-zinc-600 mb-6">Looks like this guide was moved or unpublished.</p>
        <button
          onClick={() => navigate("/instant-estimate")}
          className="h-12 px-6 rounded-full bg-[#207024] text-white font-semibold"
        >
          Get an instant quote instead
        </button>
      </div>
    );
  }

  return (
    <div className={`min-h-screen bg-white ${isGuide ? "pb-12" : "pb-32"}`}>
      {/* Thin header strip — not the admin TopHeader (public page). */}
      <header className="bg-gradient-to-r from-[#207024] to-[#2a8a2e] text-white">
        <div className="max-w-2xl mx-auto px-4 py-3 flex items-center justify-between">
          <a href="https://jobs.screenzz.com" className="flex items-center gap-2">
            <img
              src="https://customer-assets.emergentagent.com/job_window-screen-mgmt/artifacts/xq82sr3z_ScreenzZ-1024x1024.png"
              alt="ScreenzZ"
              className="h-8 w-8 rounded bg-white p-0.5"
            />
            <span className="font-barlow text-lg font-bold uppercase tracking-wide">
              ScreenzZ
            </span>
          </a>
          {isGuide ? (
            <a href="tel:+18555004416" className="h-9 px-3 rounded-full bg-white/15 border border-white/20 text-white text-sm font-semibold flex items-center"
              data-testid="header-call-cta">
              (855) 500-4416
            </a>
          ) : (
            <button
              onClick={() => goToQuote()}
              className="h-9 px-3 rounded-full bg-white/15 border border-white/20 text-white text-sm font-semibold"
              data-testid="header-quote-cta"
            >
              Instant quote
            </button>
          )}
        </div>
      </header>

      <article className="max-w-2xl mx-auto px-4 pt-6">
        {article.hero_image && (
          <img
            src={article.hero_image}
            alt={article.title}
            className="w-full aspect-[16/9] object-cover rounded-xl mb-5"
          />
        )}
        <h1
          className="text-3xl sm:text-4xl font-bold text-zinc-900 leading-tight tracking-tight"
          data-testid="article-title"
        >
          {article.title}
        </h1>
        {article.description && (
          <p className="mt-3 text-lg text-zinc-600 leading-relaxed">
            {article.description}
          </p>
        )}

        <div className="mt-8 space-y-5" data-testid="article-blocks">
          {(article.blocks || []).map((block) => (
            <Block key={block.id} block={block} onQuote={goToQuote} />
          ))}
        </div>

        {isGuide && article.measurement_form?.enabled && <MeasurementSubmitForm article={article} />}

        {/* Related articles footer */}
        {related.length > 1 && (
          <section className="mt-14 border-t pt-6">
            <h3 className="text-lg font-bold text-zinc-900 mb-3">
              {isGuide ? "More how-to guides" : "Keep reading"}
            </h3>
            <div className="space-y-2">
              {related
                .filter((r) => r.slug !== slug)
                .slice(0, 3)
                .map((r) => (
                  <a
                    key={r.slug}
                    href={`/learn/${r.slug}`}
                    className="block bg-zinc-50 border border-zinc-200 rounded-lg p-3 hover:border-[#207024]/40"
                    data-testid={`related-${r.slug}`}
                  >
                    <div className="font-semibold text-zinc-900">{r.title}</div>
                    {r.description && (
                      <div className="text-sm text-zinc-600 mt-0.5">
                        {r.description}
                      </div>
                    )}
                  </a>
                ))}
            </div>
          </section>
        )}
      </article>

      {/* Sticky mobile CTA bar — the single most-converting element on
          an advertorial. Thin, legible, one-tap. */}
      {!isGuide && <div
        className="fixed bottom-0 inset-x-0 bg-white border-t border-zinc-200 shadow-[0_-4px_12px_rgba(0,0,0,0.06)] pt-2 pb-[calc(env(safe-area-inset-bottom)+0.5rem)] z-50"
        data-testid="sticky-quote-cta"
      >
        <div className="max-w-2xl mx-auto px-4">
          <button
            onClick={() => goToQuote()}
            className="w-full h-12 rounded-full bg-[#207024] hover:bg-[#1a5c1d] text-white font-semibold text-base"
          >
            Get my instant quote
          </button>
        </div>
      </div>}
    </div>
  );
}

// --- Block renderers -----------------------------------------------------
function Block({ block, onQuote }) {
  const data = block.data || {};
  switch (block.type) {
    case "heading": {
      const level = data.level || 2;
      const Tag = `h${Math.min(Math.max(level, 2), 4)}`;
      const cls =
        level === 2
          ? "text-2xl font-bold text-zinc-900 mt-4"
          : level === 3
          ? "text-xl font-bold text-zinc-900 mt-3"
          : "text-lg font-semibold text-zinc-900 mt-2";
      return <Tag className={cls}>{data.text}</Tag>;
    }
    case "paragraph":
      return (
        <p className="text-zinc-700 leading-relaxed text-[17px]">
          {data.text}
        </p>
      );
    case "image":
      return data.src ? (
        <figure className="my-2">
          <img
            src={data.src}
            alt={data.alt || ""}
            className="w-full rounded-xl border border-zinc-200"
          />
          {data.caption && (
            <figcaption className="text-xs text-zinc-500 mt-1 italic">
              {data.caption}
            </figcaption>
          )}
        </figure>
      ) : null;
    case "comparison_table": {
      const cols = data.columns || [];
      const rows = data.rows || [];
      return (
        <div className="my-4 overflow-x-auto">
          <table className="w-full text-sm border border-zinc-200 rounded-lg overflow-hidden">
            <thead className="bg-zinc-50 text-zinc-700">
              <tr>
                <th className="text-left px-3 py-2 border-b border-zinc-200"></th>
                {cols.map((c) => (
                  <th
                    key={c}
                    className="text-left px-3 py-2 border-b border-zinc-200"
                  >
                    {c}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, i) => (
                <tr key={i} className={i % 2 ? "bg-zinc-50/50" : ""}>
                  <td className="px-3 py-2 font-medium text-zinc-800 border-b border-zinc-100">
                    {row[0]}
                  </td>
                  {row.slice(1).map((cell, j) => (
                    <td
                      key={j}
                      className="px-3 py-2 text-zinc-700 border-b border-zinc-100"
                    >
                      {cell}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    }
    case "faq":
      return (
        <div className="my-4 space-y-3">
          {(data.items || []).map((it, i) => (
            <div
              key={i}
              className="bg-zinc-50 border border-zinc-200 rounded-lg p-3"
            >
              <div className="font-semibold text-zinc-900">{it.q}</div>
              <div className="text-zinc-700 mt-1 text-sm">{it.a}</div>
            </div>
          ))}
        </div>
      );
    case "cta_button":
      return (
        <div className="my-5">
          <button
            onClick={() => onQuote(data.preferred_product)}
            className="h-12 px-6 rounded-full bg-[#207024] hover:bg-[#1a5c1d] text-white font-semibold shadow-sm"
            data-testid="inline-quote-cta"
          >
            {data.label || "Get my instant quote"}
          </button>
        </div>
      );
    case "testimonial_placeholder":
      return (
        <div className="my-4 border-2 border-dashed border-zinc-300 rounded-xl p-4 text-center text-sm text-zinc-500 italic">
          {data.note || "Verified customer review will appear here."}
        </div>
      );
    case "video": {
      const id = youtubeId(data.url);
      if (!id) return null;
      return (
        <figure className="my-2" data-testid="video-block">
          <div className="relative w-full aspect-video rounded-xl overflow-hidden bg-zinc-900">
            <iframe
              src={`https://www.youtube-nocookie.com/embed/${id}?rel=0&playsinline=1`}
              title={data.caption || "Video"}
              className="absolute inset-0 w-full h-full"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          </div>
          {data.caption && <figcaption className="text-xs text-zinc-500 mt-1 italic">{data.caption}</figcaption>}
        </figure>
      );
    }
    case "steps":
      return (
        <ol className="my-4 space-y-5" data-testid="steps-block">
          {(data.items || []).map((it, i) => (
            <li key={i} className="flex gap-4">
              <span className="w-9 h-9 rounded-full bg-[#207024] text-white font-bold flex items-center justify-center shrink-0">
                {i + 1}
              </span>
              <div className="flex-1 min-w-0 pt-1">
                {it.title && <div className="font-bold text-zinc-900 text-lg leading-snug">{it.title}</div>}
                {it.text && <p className="text-zinc-700 leading-relaxed mt-1 whitespace-pre-line">{it.text}</p>}
                {it.image && (
                  <img src={it.image} alt={it.title || `Step ${i + 1}`} className="mt-3 w-full rounded-xl border border-zinc-200" />
                )}
              </div>
            </li>
          ))}
        </ol>
      );
    case "related_articles":
      return null; // rendered globally below
    default:
      return null;
  }
}
